export type JournalPeriodKey = "today" | "7" | "30" | "365";

export const PERIODS: { key: JournalPeriodKey; label: string }[] = [
  {key: "today", label: "Сегодня"},
  {key: "7", label: "7 дней"},
  {key: "30", label: "30 дней"},
  {key: "365", label: "Год"},
];

export const getPeriodRange = (key: JournalPeriodKey, now = new Date()): { from: Date; to: Date } => {
  const to = new Date(now);
  if (key === "today") {
    const from = new Date(now);
    from.setHours(0, 0, 0, 0);
    return {from, to};
  }
  const from = new Date(now.getTime() - Number(key) * 24 * 60 * 60 * 1000);
  return {from, to};
};

const pad = (value: number): string => String(value).padStart(2, "0");

const MONTHS = ["января", "февраля", "марта", "апреля", "мая", "июня", "июля", "августа", "сентября", "октября", "ноября", "декабря"];
const WEEKDAYS = ["воскресенье", "понедельник", "вторник", "среда", "четверг", "пятница", "суббота"];

export const toDate = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatTime = (value: string | Date | null | undefined, withSeconds = false): string => {
  const date = value instanceof Date ? value : toDate(value);
  if (!date) return "";
  const base = `${pad(date.getHours())}:${pad(date.getMinutes())}`;
  return withSeconds ? `${base}:${pad(date.getSeconds())}` : base;
};

export const formatShortDate = (value: string | Date | null | undefined): string => {
  const date = value instanceof Date ? value : toDate(value);
  if (!date) return "";
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}`;
};

export const formatDateTime = (value: string | Date | null | undefined): string => {
  const date = value instanceof Date ? value : toDate(value);
  if (!date) return "";
  return `${formatShortDate(date)} ${formatTime(date)}`;
};

export const dayKey = (value: string | Date | null | undefined): string => {
  const date = value instanceof Date ? value : toDate(value);
  if (!date) return "";
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

// «28 октября, среда» / «Сегодня, 28 октября»
export const formatDayTitle = (key: string, now = new Date()): string => {
  const [year, month, day] = key.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  const text = `${day} ${MONTHS[month - 1]}${year !== now.getFullYear() ? ` ${year}` : ""}`;
  if (key === dayKey(now)) return `Сегодня, ${text}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (key === dayKey(yesterday)) return `Вчера, ${text}`;
  return `${text}, ${WEEKDAYS[date.getDay()]}`;
};

export const groupByDay = <T, >(items: T[], getTime: (item: T) => string | null | undefined): { key: string; items: T[] }[] => {
  const groups: { key: string; items: T[] }[] = [];
  for (const item of items) {
    const key = dayKey(getTime(item));
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.items.push(item);
    else groups.push({key, items: [item]});
  }
  return groups;
};

export type TimelineTick = { position: number; label: string };

// Подписи шкалы: часы — для суток, дни — до месяца, месяцы — для года
export const getTimelineTicks = (from: Date, to: Date): TimelineTick[] => {
  const span = to.getTime() - from.getTime();
  if (span <= 0) return [];
  const ticks: TimelineTick[] = [];
  const day = 24 * 60 * 60 * 1000;
  const cursor = new Date(from);

  if (span <= 2 * day) {
    cursor.setMinutes(0, 0, 0);
    const step = span <= day ? 3 : 6;
    cursor.setHours(Math.ceil(cursor.getHours() / step) * step);
    while (cursor <= to) {
      ticks.push({position: (cursor.getTime() - from.getTime()) / span, label: formatTime(cursor)});
      cursor.setHours(cursor.getHours() + step);
    }
    return ticks;
  }

  if (span <= 40 * day) {
    cursor.setHours(0, 0, 0, 0);
    cursor.setDate(cursor.getDate() + 1);
    const step = span <= 8 * day ? 1 : span <= 16 * day ? 2 : 5;
    while (cursor <= to) {
      ticks.push({position: (cursor.getTime() - from.getTime()) / span, label: formatShortDate(cursor)});
      cursor.setDate(cursor.getDate() + step);
    }
    return ticks;
  }

  cursor.setHours(0, 0, 0, 0);
  cursor.setDate(1);
  cursor.setMonth(cursor.getMonth() + 1);
  while (cursor <= to) {
    ticks.push({
      position: (cursor.getTime() - from.getTime()) / span,
      label: MONTHS[cursor.getMonth()].slice(0, 3),
    });
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return ticks;
};

export const formatDuration = (ms: number): string => {
  const minutes = Math.max(0, Math.round(ms / 60000));
  if (minutes < 1) return "меньше минуты";
  if (minutes < 60) return `${minutes} мин`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours < 24) return rest ? `${hours} ч ${rest} мин` : `${hours} ч`;
  const days = Math.floor(hours / 24);
  const restHours = hours % 24;
  return restHours ? `${days} дн. ${restHours} ч` : `${days} дн.`;
};
